/**
 * Extract the first .apk file from a ZIP archive.
 * Parses the ZIP End of Central Directory to find entries, then decompresses
 * using the browser's DecompressionStream API.
 *
 * Shared between the ADB installer (push to device) and the browser download
 * flow (download extracted APK).
 */
// `Uint8Array<ArrayBuffer>`, not bare `Uint8Array`: TS 5.7+ made TypedArrays
// generic over their buffer, and both the caller's input and every value
// returned here are ArrayBuffer-backed. Keeping the parameter narrow lets
// `slice` propagate it and keeps the result usable as a BlobPart.
export async function extractApkFromZip(
  zipData: Uint8Array<ArrayBuffer>,
): Promise<{ apkData: Uint8Array<ArrayBuffer>; apkName: string }> {
  const view = new DataView(zipData.buffer, zipData.byteOffset, zipData.byteLength);

  // Find End of Central Directory record (search backwards for signature 0x06054b50)
  let eocdOffset = -1;
  for (let i = zipData.length - 22; i >= 0; i--) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocdOffset = i;
      break;
    }
  }
  if (eocdOffset === -1) {
    throw new Error("Invalid ZIP: cannot find End of Central Directory");
  }

  const cdOffset = view.getUint32(eocdOffset + 16, true);
  const cdEntries = view.getUint16(eocdOffset + 10, true);

  // Walk Central Directory entries to find .apk file
  let pos = cdOffset;
  for (let i = 0; i < cdEntries; i++) {
    if (view.getUint32(pos, true) !== 0x02014b50) break;

    const compressionMethod = view.getUint16(pos + 10, true);
    const compressedSize = view.getUint32(pos + 20, true);
    const uncompressedSize = view.getUint32(pos + 24, true);
    const nameLen = view.getUint16(pos + 28, true);
    const extraLen = view.getUint16(pos + 30, true);
    const commentLen = view.getUint16(pos + 32, true);
    const localHeaderOffset = view.getUint32(pos + 42, true);
    const nameBytes = zipData.slice(pos + 46, pos + 46 + nameLen);
    const name = new TextDecoder().decode(nameBytes);

    if (name.endsWith(".apk")) {
      // Read local file header to find actual data offset
      const localNameLen = view.getUint16(localHeaderOffset + 26, true);
      const localExtraLen = view.getUint16(localHeaderOffset + 28, true);
      const dataOffset = localHeaderOffset + 30 + localNameLen + localExtraLen;
      const compressedData = zipData.slice(dataOffset, dataOffset + compressedSize);

      if (compressionMethod === 0) {
        // Stored (no compression)
        return { apkData: compressedData, apkName: name };
      } else if (compressionMethod === 8) {
        // Deflate — use browser DecompressionStream
        const ds = new (globalThis as any).DecompressionStream("deflate-raw");
        const writer = ds.writable.getWriter();
        writer.write(compressedData);
        writer.close();

        const decompressedChunks: Uint8Array[] = [];
        const decompReader = ds.readable.getReader();
        let decompDone = false;
        while (!decompDone) {
          const { done, value } = await decompReader.read();
          decompDone = done;
          if (!done && value) decompressedChunks.push(value);
        }

        const result = new Uint8Array(uncompressedSize);
        let off = 0;
        for (const chunk of decompressedChunks) {
          result.set(chunk, off);
          off += chunk.byteLength;
        }
        return { apkData: result, apkName: name };
      } else {
        throw new Error(`Unsupported ZIP compression method: ${compressionMethod}`);
      }
    }

    pos += 46 + nameLen + extraLen + commentLen;
  }

  throw new Error("No .apk file found inside the ZIP archive");
}

/**
 * Download a file from a URL.  If the file is a ZIP containing an APK,
 * extract the APK and trigger a browser download of the `.apk` file.
 * Otherwise, trigger a normal download of the raw file.
 *
 * Returns the final filename for UI feedback purposes.
 */
export type DownloadStep = "downloading" | "extracting" | "saving";

/** Yield to the browser so React can re-render between steps */
const tick = () => new Promise<void>((r) => setTimeout(r, 80));

export async function downloadAndExtractApk(
  url: string,
  fallbackFileName: string,
  onStep?: (step: DownloadStep) => void,
  signal?: AbortSignal,
): Promise<string> {
  onStep?.("downloading");
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`Download failed: ${response.status}`);

  const rawData = new Uint8Array(await response.arrayBuffer());

  let fileData = rawData;
  let fileName = fallbackFileName;

  // Check for ZIP header (PK\x03\x04)
  if (rawData[0] === 0x50 && rawData[1] === 0x4b) {
    try {
      onStep?.("extracting");
      await tick();
      const extracted = await extractApkFromZip(rawData);
      fileData = extracted.apkData;
      fileName = extracted.apkName.split("/").pop() || fileName;
      // Ensure .apk extension
      if (!fileName.endsWith(".apk")) fileName += ".apk";
    } catch {
      // Not a ZIP with an APK inside — download the raw file as-is
    }
  }

  onStep?.("saving");
  await tick();

  // Trigger browser download
  const blob = new Blob([fileData], { type: "application/vnd.android.package-archive" });
  const downloadUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(downloadUrl);

  return fileName;
}
